import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n/I18nContext';
import { tripSessionsAPI } from '../../services/api';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { io } from 'socket.io-client';
import 'leaflet/dist/leaflet.css';

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

const BUS_GREEN = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: markerShadow,
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const BUS_BLUE = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: markerShadow,
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const DEFAULT_CENTER = [48.8566, 2.3522];

function MapUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
}

function LiveTracking() {
  const { t } = useI18n();
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const socketRef = useRef(null);

  const loadActiveTrips = useCallback(async () => {
    try {
      const { data } = await tripSessionsAPI.active();
      setTrips(data);
      setLastRefresh(new Date());
      if (selectedTrip) {
        const updated = data.find((tr) => tr.id === selectedTrip.id);
        if (updated) setSelectedTrip(updated);
      }
    } catch (err) {
      console.error('Error loading active trips:', err);
    }
    setLoading(false);
  }, [selectedTrip]);

  useEffect(() => {
    loadActiveTrips();
    const interval = setInterval(loadActiveTrips, 10000);
    return () => clearInterval(interval);
  }, [loadActiveTrips]);

  useEffect(() => {
    const tenantId = localStorage.getItem('tenant_id');
    const socket = io(window.location.origin);
    socketRef.current = socket;

    if (tenantId) {
      socket.emit('join_tenant_room', tenantId);
    }

    socket.on('bus_location_changed', (data) => {
      setTrips((prev) => prev.map((tr) => {
        if (tr.bus_id === data.bus_id) {
          return { ...tr, latitude: data.latitude, longitude: data.longitude, last_gps_at: new Date().toISOString() };
        }
        return tr;
      }));
    });

    socket.on('student_event', (data) => {
      loadActiveTrips();
    });

    return () => socket.disconnect();
  }, []);

  const handleSelectTrip = (trip) => {
    setSelectedTrip(trip);
  };

  const activeTrips = trips.filter((tr) => tr.latitude != null && tr.longitude != null);
  const inactiveTrips = trips.filter((tr) => tr.latitude == null || tr.longitude == null);

  const defaultCenter = activeTrips.length > 0
    ? [activeTrips[0].latitude, activeTrips[0].longitude]
    : DEFAULT_CENTER;

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <Link to="/admin" style={{ color: '#007bff', textDecoration: 'none' }}>{t('backToDashboard')}</Link>
      <h1>{t('liveTracking')}</h1>

      {loading ? (
        <p>{t('loading')}</p>
      ) : trips.length === 0 ? (
        <div style={{ padding: '60px', textAlign: 'center', color: '#888', background: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <p style={{ fontSize: '18px' }}>{t('noActiveTrips')}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          {/* Sidebar */}
          <div style={{ flex: '0 0 340px', maxHeight: 'calc(100vh - 160px)', overflowY: 'auto', background: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', border: '1px solid #e0e0e0' }}>
            <div style={{ padding: '12px 15px', borderBottom: '1px solid #eee', fontWeight: 'bold', fontSize: '14px', background: '#f8f9fa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{t('activeBuses')} ({trips.length})</span>
              {lastRefresh && <span style={{ fontSize: '11px', color: '#888', fontWeight: 'normal' }}>{lastRefresh.toLocaleTimeString()}</span>}
            </div>

            {trips.map((trip) => (
              <div
                key={trip.id}
                onClick={() => handleSelectTrip(trip)}
                style={{
                  padding: '12px 15px',
                  borderBottom: '1px solid #eee',
                  cursor: 'pointer',
                  background: selectedTrip?.id === trip.id ? '#e3f2fd' : '#fff',
                  borderLeft: selectedTrip?.id === trip.id ? '3px solid #17a2b8' : '3px solid transparent',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong style={{ fontSize: '14px' }}>{trip.bus_number || '---'}</strong>
                  <span style={{
                    padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold',
                    color: trip.latitude ? '#155724' : '#856404',
                    backgroundColor: trip.latitude ? '#d4edda' : '#fff3cd',
                  }}>
                    {trip.latitude ? t('live') : t('noGpsData')}
                  </span>
                </div>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
                  {trip.route_name || '---'} | {trip.driver_name || '---'}
                </p>
                <div style={{ display: 'flex', gap: '8px', marginTop: '6px', fontSize: '11px' }}>
                  <span style={{ color: '#28a745', fontWeight: 'bold' }}>{t('pickedUp')}: {trip.students_picked_up}</span>
                  <span style={{ color: '#007bff', fontWeight: 'bold' }}>{t('droppedOff')}: {trip.students_dropped_off}</span>
                  <span style={{ color: '#dc3545', fontWeight: 'bold' }}>{t('remaining')}: {trip.students_remaining}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Main content */}
          <div style={{ flex: '1 1 500px' }}>
            {selectedTrip ? (
              <div>
                <h3 style={{ marginTop: 0 }}>
                  {selectedTrip.bus_number} ({selectedTrip.license_plate}) — {selectedTrip.route_name}
                </h3>
                <p style={{ fontSize: '13px', color: '#666', margin: '4px 0 12px 0' }}>
                  {t('driver')}: {selectedTrip.driver_name || '---'} | {t('startedAt')}: {new Date(selectedTrip.started_at).toLocaleString()}
                </p>

                {/* Stats bar */}
                <div style={{ display: 'flex', gap: '12px', marginBottom: '15px', flexWrap: 'wrap' }}>
                  <div style={{ ...statCard, borderLeft: '4px solid #6c757d' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{selectedTrip.students_total}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{t('totalStudents')}</div>
                  </div>
                  <div style={{ ...statCard, borderLeft: '4px solid #28a745' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>{selectedTrip.students_picked_up}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{t('pickedUp')}</div>
                  </div>
                  <div style={{ ...statCard, borderLeft: '4px solid #007bff' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>{selectedTrip.students_dropped_off}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{t('droppedOff')}</div>
                  </div>
                  <div style={{ ...statCard, borderLeft: '4px solid #dc3545' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545' }}>{selectedTrip.students_remaining}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{t('inBus')}</div>
                  </div>
                </div>

                {/* Map */}
                {selectedTrip.latitude != null ? (
                  <div style={{ borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '15px' }}>
                    <MapContainer center={[selectedTrip.latitude, selectedTrip.longitude]} zoom={15} style={{ height: '350px', width: '100%' }}>
                      <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                      <MapUpdater center={[selectedTrip.latitude, selectedTrip.longitude]} />
                      <Marker position={[selectedTrip.latitude, selectedTrip.longitude]} icon={BUS_GREEN}>
                        <Popup>
                          <strong>{selectedTrip.bus_number}</strong><br />
                          {selectedTrip.route_name}<br />
                          {selectedTrip.driver_name}
                        </Popup>
                      </Marker>
                    </MapContainer>
                  </div>
                ) : (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#888', background: '#fff', borderRadius: '8px', marginBottom: '15px', border: '1px dashed #ddd' }}>
                    {t('waitingGps')}
                  </div>
                )}

                {/* Student list */}
                {selectedTrip.students && selectedTrip.students.length > 0 && (
                  <div style={{ background: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
                    <div style={{ padding: '12px 15px', borderBottom: '1px solid #eee', fontWeight: 'bold', fontSize: '14px', background: '#f8f9fa' }}>
                      {t('studentsOnRoute')} ({selectedTrip.students.length})
                    </div>
                    {selectedTrip.students.map((student) => {
                      const statusColor = student.status === 'pickup' ? '#28a745' : student.status === 'dropoff' ? '#007bff' : '#6c757d';
                      const statusBg = student.status === 'pickup' ? '#d4edda' : student.status === 'dropoff' ? '#d1ecf1' : '#f8f9fa';
                      const statusText = student.status === 'pickup' ? t('pickedUp') : student.status === 'dropoff' ? t('droppedOff') : t('pending');
                      return (
                        <div key={student.id} style={{ padding: '10px 15px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', background: statusColor }} />
                            <span style={{ fontSize: '14px' }}>{student.first_name} {student.last_name}</span>
                          </div>
                          <span style={{
                            padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold',
                            color: statusColor, backgroundColor: statusBg,
                          }}>
                            {statusText}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ padding: '60px', textAlign: 'center', color: '#888', background: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <p style={{ fontSize: '16px' }}>{t('selectBusToTrack')}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const statCard = { background: '#fff', padding: '15px 20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', minWidth: '120px' };

export default LiveTracking;
